import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

/**
 * Button — the primary interactive control.
 * One accent OR one positive (go) action per view; ghost is the default for
 * everything secondary. `bad` is the destructive treatment.
 */
export const buttonVariants = cva("btn", {
  variants: {
    variant: {
      accent: "btn--accent",
      go: "btn--go",
      ghost: "btn--ghost",
      bad: "btn--bad",
    },
    size: { md: "", sm: "btn--sm" },
  },
  defaultVariants: { variant: "ghost", size: "md" },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: LucideIcon;
}

export function Button({
  className,
  variant,
  size,
  icon,
  children,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {icon && <Icon icon={icon} size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
}

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  /** Required: icon-only controls must always carry an accessible label. */
  label: string;
  iconSize?: number;
}

export function IconButton({
  className,
  icon,
  label,
  iconSize = 16,
  type = "button",
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={cn("iconbtn", className)}
      aria-label={label}
      title={label}
      {...props}
    >
      <Icon icon={icon} size={iconSize} />
    </button>
  );
}
